/**
 * Dialog d'aperçu d'un SMS avant envoi (iter43) — ÉDITABLE depuis iter230.
 *
 * Affiche une bulle SMS façon iMessage avec le numéro destinataire + le message,
 * que l'utilisateur peut personnaliser avant l'envoi. Utilisé par :
 *   - Envoi / renvoi du devis par SMS (barre d'actions OR)
 *   - Bascule statut → devis_envoye (choix "Avec SMS")
 *   - Bascule statut → pret (choix "Avec SMS") et renvoi "véhicule prêt"
 *
 * Pour le SMS devis, le texte contient le placeholder `{lien}` : il est remplacé
 * par le lien sécurisé à l'envoi (et ajouté à la fin s'il a été supprimé).
 * L'envoi réel est confié au parent via la callback onSend.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, X, Smartphone, Loader2, Pencil, RotateCcw, Link2 } from "lucide-react";

export default function SmsPreviewDialog({
  open,
  onClose,
  onSend,
  onMessageChange,
  to,
  message,
  lienAuto = false,
  loading = false,
  sending = false,
  title = "Aperçu du SMS avant envoi",
  sendLabel = "Envoyer le SMS",
}) {
  const [editing, setEditing] = useState(false);
  const [initial, setInitial] = useState("");

  // Mémorise le texte d'origine (pour le bouton « Texte d'origine ») dès que
  // l'aperçu est chargé, et remet le mode aperçu à chaque ouverture.
  useEffect(() => {
    if (!open) { setEditing(false); return; }
    if (message && !initial) setInitial(message);
  }, [open, message, initial]);
  useEffect(() => { if (!open) setInitial(""); }, [open]);

  const len = (message || "").length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="max-w-md" data-testid="sms-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#0891b2]" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>À</span>
            <span className="font-mono text-gray-800" data-testid="sms-preview-to">{to || "—"}</span>
          </div>

          {editing ? (
            <Textarea
              value={message || ""}
              onChange={(e) => onMessageChange?.(e.target.value)}
              rows={6}
              className="bg-white text-sm"
              placeholder="Votre message…"
              data-testid="sms-preview-textarea"
            />
          ) : (
            <div className="flex justify-end">
              <div
                className="max-w-[85%] bg-[#0891b2] text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 shadow-sm whitespace-pre-line"
                data-testid="sms-preview-message"
              >
                {loading ? "Chargement de l'aperçu…" : (message || "(message vide)")}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              <Button
                type="button" size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setEditing((v) => !v)}
                disabled={sending}
                data-testid="sms-preview-edit-toggle"
              >
                <Pencil className="w-3 h-3 mr-1" /> {editing ? "Aperçu" : "Personnaliser"}
              </Button>
              {message !== initial && (
                <Button
                  type="button" size="sm" variant="ghost" className="h-7 text-xs text-gray-500"
                  onClick={() => onMessageChange?.(initial)}
                  disabled={sending}
                  data-testid="sms-preview-reset"
                >
                  <RotateCcw className="w-3 h-3 mr-1" /> Texte d'origine
                </Button>
              )}
            </div>
            <div className="text-[10px] text-gray-400">
              ~{len} caractères · {Math.max(1, Math.ceil(len / 160))} SMS
            </div>
          </div>
        </div>

        {lienAuto && (
          <p className="text-xs text-[#0e7490] bg-cyan-50 border border-cyan-100 rounded p-2 flex items-start gap-1.5" data-testid="sms-preview-lien-note">
            <Link2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <code className="font-mono">{"{lien}"}</code> sera remplacé par le lien sécurisé du devis.
              Si vous le supprimez, le lien sera ajouté à la fin du message.
            </span>
          </p>
        )}

        <p className="text-xs text-gray-500">
          Le SMS sera envoyé au numéro ci-dessus via Twilio. En environnement preview, l'envoi
          est mocké (aucun coût).
        </p>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            data-testid="sms-preview-cancel"
          >
            <X className="w-4 h-4 mr-1.5" /> Annuler
          </Button>
          <Button
            onClick={onSend}
            disabled={sending || !to || !(message || "").trim()}
            className="bg-[#0891b2] hover:bg-[#0e7490]"
            data-testid="sms-preview-send"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1.5" />
            )}
            {sendLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
